import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Plus, Pencil, Trash2, Copy, Upload, ChevronLeft, ChevronRight, Clock, Users, X, DollarSign, Coins, RefreshCw } from "lucide-react";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_MAP = { "Sunday": 0, "Monday": 1, "Tuesday": 2, "Wednesday": 3, "Thursday": 4, "Friday": 5, "Saturday": 6 };

function getWeekDates(startDate, weekStartDay = 0) {
  const d = new Date(startDate);
  const currentDay = d.getDay();
  const diff = (currentDay - weekStartDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const dd = new Date(d);
    dd.setDate(d.getDate() + i);
    days.push(dd);
  }
  return days;
}

function fmtDate(d) { return d.toISOString().split("T")[0]; }

function fmtDay(d) {
  const names = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return names[d.getDay()];
}

function fmtFullDay(d) {
  const names = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return names[d.getDay()];
}

function fmtDateShort(d) {
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}

function to12h(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return `${h12}:${m}${ampm}`;
}

function calcHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round(mins / 60 * 100) / 100;
}

export default function SchedulingPage() {
  const [activeTab, setActiveTab] = useState("schedule"); // "schedule" | "timeworked" | "payroll" | "tippool"
  const [weekStartDay, setWeekStartDay] = useState(0);
  const [weekStart, setWeekStart] = useState(() => new Date());
  const [shifts, setShifts] = useState([]);
  const [workedShifts, setWorkedShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [posRoles, setPosRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("all");
  const [hideEmpty, setHideEmpty] = useState(false);
  const [showWages, setShowWages] = useState(true);
  const [timesheetRules, setTimesheetRules] = useState({});

  // Tip Pool
  const [tipPoolData, setTipPoolData] = useState(null);
  const [tipPoolAdj, setTipPoolAdj] = useState(0);
  const [tipPoolLoading, setTipPoolLoading] = useState(false);

  // Shift edit dialog
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({ employee_id: "", date: "", start_time: "", end_time: "", pos_role_id: "", notes: "" });

  // Copy shifts dialog
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyTarget, setCopyTarget] = useState("");
  const [copying, setCopying] = useState(false);

  // Import dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Time Worked edit dialog
  const [showTWEdit, setShowTWEdit] = useState(false);
  const [twEditEmp, setTwEditEmp] = useState(null); // { employee, date }
  const [twEditShifts, setTwEditShifts] = useState([]); // editable copies of shifts for that emp+day
  const [twSaving, setTwSaving] = useState(false);

  const weekDates = getWeekDates(weekStart, weekStartDay);
  const dateFrom = fmtDate(weekDates[0]);
  const dateTo = fmtDate(weekDates[6]);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, eRes, rRes, tsRes, twRes] = await Promise.all([
        api.get(`/schedule/shifts?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get("/employees"),
        api.get("/pos-roles"),
        api.get("/timesheet-rules"),
        api.get(`/timeclock/shifts?date_from=${dateFrom}T00:00:00&date_to=${dateTo}T23:59:59&limit=2000`),
      ]);
      setShifts(sRes.data);
      setEmployees(eRes.data.filter((e) => e.is_active !== false));
      setPosRoles(rRes.data.filter((r) => r.is_active !== false));
      const wsd = DAY_MAP[tsRes.data?.week_starts_on] ?? 0;
      setWeekStartDay(wsd);
      setTimesheetRules(tsRes.data || {});
      setWorkedShifts(twRes.data || []);
    } catch { toast.error("Failed to load schedule"); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const thisWeek = () => { setWeekStart(new Date()); };

  // Filter employees by role
  const filteredEmployees = filterRole === "all"
    ? employees
    : employees.filter((e) => (e.roles_wages || []).some((rw) => rw.pos_role_id === filterRole));

  // Build grid data: { employee_id: { date: [shift, shift, ...] } }
  const grid = {};
  for (const emp of filteredEmployees) {
    const empId = emp.user_id;
    grid[empId] = {};
    for (const d of weekDates) {
      grid[empId][fmtDate(d)] = [];
    }
  }
  for (const s of shifts) {
    if (grid[s.employee_id]) {
      if (grid[s.employee_id][s.date]) {
        grid[s.employee_id][s.date].push(s);
      }
    }
  }

  // Calculate totals per employee
  const empTotals = {};
  for (const emp of filteredEmployees) {
    let totalHours = 0;
    let totalWage = 0;
    const empShifts = shifts.filter((s) => s.employee_id === emp.user_id);
    for (const s of empShifts) {
      totalHours += s.scheduled_hours || 0;
      totalWage += (s.scheduled_hours || 0) * (s.hourly_rate || 0);
    }
    empTotals[emp.user_id] = { hours: Math.round(totalHours * 100) / 100, wage: Math.round(totalWage * 100) / 100 };
  }

  // Filter out employees with no shifts if hideEmpty
  const displayEmployees = hideEmpty
    ? filteredEmployees.filter((e) => empTotals[e.user_id]?.hours > 0)
    : filteredEmployees;

  // Shift dialog handlers
  const openCreateShift = (empId, date) => {
    const emp = employees.find((e) => e.user_id === empId);
    const defaultRole = emp?.roles_wages?.[0]?.pos_role_id || "";
    setEditingShift(null);
    setShiftForm({ employee_id: empId, date, start_time: "09:00", end_time: "17:00", pos_role_id: defaultRole, notes: "" });
    setShowShiftDialog(true);
  };

  const openEditShift = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      employee_id: shift.employee_id, date: shift.date,
      start_time: shift.start_time, end_time: shift.end_time,
      pos_role_id: shift.pos_role_id || "", notes: shift.notes || "",
    });
    setShowShiftDialog(true);
  };

  const saveShift = async () => {
    if (!shiftForm.employee_id || !shiftForm.date || !shiftForm.start_time || !shiftForm.end_time) {
      toast.error("All fields required"); return;
    }
    try {
      if (editingShift) {
        await api.put(`/schedule/shifts/${editingShift.scheduled_shift_id}`, shiftForm);
        toast.success("Shift updated");
      } else {
        await api.post("/schedule/shifts", shiftForm);
        toast.success("Shift created");
      }
      setShowShiftDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const deleteShift = async (shiftId) => {
    try {
      await api.delete(`/schedule/shifts/${shiftId}`);
      toast.success("Shift deleted");
      fetchData();
    } catch { toast.error("Delete failed"); }
  };

  // Copy shifts
  const handleCopy = async () => {
    if (!copyTarget) { toast.error("Select a target week"); return; }
    setCopying(true);
    try {
      const res = await api.post("/schedule/copy-shifts", {
        source_from: dateFrom, source_to: dateTo, target_start: copyTarget,
      });
      toast.success(res.data.message);
      setShowCopyDialog(false);
      // Navigate to copied week
      setWeekStart(new Date(copyTarget + "T12:00:00"));
    } catch (err) { toast.error(err.response?.data?.detail || "Copy failed"); }
    finally { setCopying(false); }
  };

  // Import CSV
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/schedule/import-csv", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        res.data.errors.slice(0, 3).forEach((err) => toast.error(err));
      }
      setShowImportDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  // Next week date for copy default
  const nextWeekStart = new Date(weekStart);
  nextWeekStart.setDate(nextWeekStart.getDate() + 7);

  // Time Worked edit helpers
  const openTWEdit = (emp, dateStr) => {
    const dayShifts = (workedShifts || []).filter((ws) => {
      if (ws.employee_id !== emp.user_id) return false;
      const ciDate = ws.clock_in ? ws.clock_in.split("T")[0] : null;
      return ciDate === dateStr;
    });
    setTwEditEmp({ employee: emp, date: dateStr });
    setTwEditShifts(dayShifts.map((ws) => ({
      shift_id: ws.shift_id,
      clock_in: ws.clock_in || "",
      clock_out: ws.clock_out || "",
      pos_role_id: ws.pos_role_id || "",
      pos_role_name: ws.pos_role_name || "",
      hourly_rate: ws.hourly_rate || 0,
      remarks: ws.remarks || "",
      hours_worked: ws.hours_worked || 0,
      _isOpen: ws.clock_out === null,
      _deleted: false,
    })));
    setShowTWEdit(true);
  };

  const twUpdateField = (idx, field, value) => {
    setTwEditShifts((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const twMarkDelete = (idx) => {
    setTwEditShifts((prev) => prev.map((s, i) => i === idx ? { ...s, _deleted: !s._deleted } : s));
  };

  const twAddNew = () => {
    if (!twEditEmp) return;
    const dateStr = twEditEmp.date;
    const emp = twEditEmp.employee;
    const defaultRole = emp?.roles_wages?.[0]?.pos_role_id || "";
    const defaultRate = emp?.roles_wages?.[0]?.hourly_rate || 0;
    setTwEditShifts((prev) => [...prev, {
      shift_id: `new_${Date.now()}`,
      clock_in: `${dateStr}T09:00:00`,
      clock_out: `${dateStr}T17:00:00`,
      pos_role_id: defaultRole,
      pos_role_name: "",
      hourly_rate: defaultRate,
      remarks: "Manual entry",
      hours_worked: 8,
      _isOpen: false,
      _deleted: false,
      _isNew: true,
    }]);
  };

  const twSaveAll = async () => {
    setTwSaving(true);
    try {
      for (const s of twEditShifts) {
        if (s._isNew && s._deleted) continue; // new but marked deleted, skip
        if (s._isNew) {
          await api.post("/timeclock/manual-entry", {
            employee_id: twEditEmp.employee.user_id,
            clock_in: s.clock_in,
            clock_out: s.clock_out || null,
            pos_role_id: s.pos_role_id,
            hourly_rate: parseFloat(s.hourly_rate) || 0,
            remarks: s.remarks || "",
          });
        } else if (s._deleted) {
          await api.delete(`/timeclock/shifts/${s.shift_id}`);
        } else {
          await api.put(`/timeclock/shifts/${s.shift_id}`, {
            clock_in: s.clock_in,
            clock_out: s.clock_out || null,
            pos_role_id: s.pos_role_id,
            hourly_rate: parseFloat(s.hourly_rate) || 0,
            remarks: s.remarks || "",
          });
        }
      }
      toast.success("Shifts updated");
      setShowTWEdit(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setTwSaving(false); }
  };

  // Convert ISO datetime to time input value (HH:MM)
  const isoToTimeInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  // Convert time input + original ISO to new ISO
  const timeInputToIso = (timeStr, originalIso) => {
    if (!timeStr) return originalIso;
    const d = originalIso ? new Date(originalIso) : new Date();
    const [h, m] = timeStr.split(":").map(Number);
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  };

  // Tip Pool fetch
  const fetchTipPool = useCallback(async () => {
    setTipPoolLoading(true);
    try {
      const res = await api.get(`/tip-pool/calculate?date_from=${dateFrom}&date_to=${dateTo}`);
      setTipPoolData(res.data);
      setTipPoolAdj(0);
    } catch { toast.error("Failed to calculate tip pool"); }
    finally { setTipPoolLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { if (activeTab === "tippool") fetchTipPool(); }, [activeTab, fetchTipPool]);

  if (loading) return <div className="p-6 text-slate-500" data-testid="scheduling-loading">Loading schedule...</div>;

  // ===== TIME WORKED GRID DATA =====
  const twByEmpDay = {}; // { employee_id: { date: [{ clock_in, clock_out, hours, rate }] } }
  const twEmpTotals = {}; // { employee_id: { hours, wage } }
  for (const emp of filteredEmployees) {
    twByEmpDay[emp.user_id] = {};
    twEmpTotals[emp.user_id] = { hours: 0, wage: 0 };
    for (const d of weekDates) twByEmpDay[emp.user_id][fmtDate(d)] = [];
  }
  for (const ws of workedShifts) {
    if (!twByEmpDay[ws.employee_id]) continue;
    const ciDate = ws.clock_in ? ws.clock_in.split("T")[0] : null;
    if (!ciDate) continue;
    if (twByEmpDay[ws.employee_id][ciDate]) {
      twByEmpDay[ws.employee_id][ciDate].push(ws);
    }
    const hrs = ws.hours_worked || 0;
    const rate = ws.hourly_rate || 0;
    twEmpTotals[ws.employee_id].hours += hrs;
    twEmpTotals[ws.employee_id].wage += hrs * rate;
  }
  // Also count open shifts (clock_out is null)
  for (const ws of workedShifts) {
    if (ws.clock_out === null && twByEmpDay[ws.employee_id]) {
      const ciDate = ws.clock_in ? ws.clock_in.split("T")[0] : null;
      if (ciDate && twByEmpDay[ws.employee_id][ciDate]) {
        // hours_worked is 0 for open shifts, compute live
        const ciTime = new Date(ws.clock_in);
        const now = new Date();
        const liveHours = (now - ciTime) / 3600000;
        twEmpTotals[ws.employee_id].hours += liveHours;
        twEmpTotals[ws.employee_id].wage += liveHours * (ws.hourly_rate || 0);
      }
    }
  }
  Object.keys(twEmpTotals).forEach((k) => {
    twEmpTotals[k].hours = Math.round(twEmpTotals[k].hours * 100) / 100;
    twEmpTotals[k].wage = Math.round(twEmpTotals[k].wage * 100) / 100;
  });
  const twDisplayEmployees = hideEmpty
    ? filteredEmployees.filter((e) => twEmpTotals[e.user_id]?.hours > 0)
    : filteredEmployees;

  const fmtClockTime = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    const h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${m}${ampm}`;
  };

  return (
    <div className="p-4 max-w-full" data-testid="scheduling-page">
      {/* Tab Bar */}
      <div className="flex items-center gap-1 mb-4">
        <button onClick={() => setActiveTab("schedule")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === "schedule" ? "bg-[var(--brand-primary)] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid="tab-schedule">
          <CalendarDays className="h-4 w-4" /> Shift Schedule
        </button>
        <button onClick={() => setActiveTab("timeworked")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === "timeworked" ? "bg-[var(--brand-primary)] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid="tab-timeworked">
          <Clock className="h-4 w-4" /> Time Worked
        </button>
        <button onClick={() => setActiveTab("payroll")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === "payroll" ? "bg-[var(--brand-primary)] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid="tab-payroll">
          <DollarSign className="h-4 w-4" /> Payroll
        </button>
        <button onClick={() => setActiveTab("tippool")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === "tippool" ? "bg-[var(--brand-primary)] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid="tab-tippool">
          <Coins className="h-4 w-4" /> Tip Pooling
        </button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            {activeTab === "schedule" && <><CalendarDays className="h-5 w-5" /> Shift Schedule</>}
            {activeTab === "timeworked" && <><Clock className="h-5 w-5" /> Time Worked</>}
            {activeTab === "payroll" && <><DollarSign className="h-5 w-5" /> Payroll</>}
            {activeTab === "tippool" && <><Coins className="h-5 w-5" /> Tip Pooling</>}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">{fmtDateShort(weekDates[0])} — {fmtDateShort(weekDates[6])}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Role Filter */}
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="schedule-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {posRoles.map((r) => <SelectItem key={r.pos_role_id} value={r.pos_role_id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Week nav */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1">
            <button onClick={prevWeek} className="p-1.5 hover:bg-slate-100 rounded" data-testid="schedule-prev-week"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={thisWeek} className="px-2 py-1 text-xs font-medium hover:bg-slate-100 rounded" data-testid="schedule-today">Today</button>
            <button onClick={nextWeek} className="p-1.5 hover:bg-slate-100 rounded" data-testid="schedule-next-week"><ChevronRight className="h-4 w-4" /></button>
          </div>

          {/* Toggles */}
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <Switch checked={hideEmpty} onCheckedChange={setHideEmpty} className="scale-75" data-testid="schedule-hide-empty" />
            Hide empty
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <Switch checked={showWages} onCheckedChange={setShowWages} className="scale-75" data-testid="schedule-show-wages" />
            Wages
          </label>

          {/* Actions - Schedule tab only */}
          {activeTab === "schedule" && (
            <>
              <Button variant="outline" size="sm" onClick={() => { setCopyTarget(fmtDate(nextWeekStart)); setShowCopyDialog(true); }} className="text-xs h-8" data-testid="schedule-copy-btn">
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy Week
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowImportDialog(true)} className="text-xs h-8" data-testid="schedule-import-btn">
                <Upload className="h-3.5 w-3.5 mr-1" /> Import
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ===== SHIFT SCHEDULE TAB ===== */}
      {activeTab === "schedule" && (
        <>
      {/* Weekly Grid */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="schedule-grid">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-600 px-3 py-2.5 w-36 sticky left-0 bg-slate-50 z-10">Employee</th>
              <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 w-16">Hours</th>
              {showWages && <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 w-16">Wage</th>}
              {weekDates.map((d) => (
                <th key={fmtDate(d)} className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">
                  <div>{fmtDay(d)}</div>
                  <div className="text-[10px] font-normal text-slate-400">{fmtDateShort(d)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayEmployees.length === 0 ? (
              <tr><td colSpan={9 + (showWages ? 1 : 0)} className="text-center py-12 text-slate-400 text-sm">No employees with scheduled shifts this week</td></tr>
            ) : displayEmployees.map((emp) => {
              const empId = emp.user_id;
              const totals = empTotals[empId] || { hours: 0, wage: 0 };
              return (
                <tr key={empId} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`schedule-row-${empId}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white z-10">
                    <div className="text-sm font-medium text-slate-800 truncate max-w-[130px]">
                      {emp.last_name && emp.first_name ? `${emp.last_name}, ${emp.first_name}` : emp.name}
                    </div>
                  </td>
                  <td className="text-center text-xs font-bold text-slate-700 px-2">{totals.hours > 0 ? totals.hours.toFixed(2) : "-"}</td>
                  {showWages && <td className="text-center text-xs font-bold text-slate-700 px-2">{totals.wage > 0 ? `$${totals.wage.toFixed(2)}` : "-"}</td>}
                  {weekDates.map((d) => {
                    const dateStr = fmtDate(d);
                    const dayShifts = grid[empId]?.[dateStr] || [];
                    return (
                      <td key={dateStr} className="px-1 py-1.5 align-top min-w-[110px]">
                        <div className="space-y-1">
                          {dayShifts.map((s) => (
                            <div key={s.scheduled_shift_id}
                              className="group relative bg-blue-50 border border-blue-200 rounded px-1.5 py-1 text-[11px] text-blue-800 cursor-pointer hover:bg-blue-100 transition-colors"
                              onClick={() => openEditShift(s)}
                              data-testid={`shift-cell-${s.scheduled_shift_id}`}
                            >
                              <div className="font-medium">{to12h(s.start_time)} - {to12h(s.end_time)}</div>
                              {s.pos_role_name && <div className="text-[9px] text-blue-600">{s.pos_role_name}</div>}
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteShift(s.scheduled_shift_id); }}
                                className="absolute top-0.5 right-0.5 h-4 w-4 rounded bg-red-100 text-red-500 items-center justify-center hover:bg-red-200 hidden group-hover:flex"
                                data-testid={`delete-shift-${s.scheduled_shift_id}`}
                              ><X className="h-2.5 w-2.5" /></button>
                            </div>
                          ))}
                          <button
                            onClick={() => openCreateShift(empId, dateStr)}
                            className="w-full h-6 rounded border border-dashed border-slate-200 text-slate-300 hover:text-slate-500 hover:border-slate-400 hover:bg-slate-50 flex items-center justify-center transition-colors"
                            data-testid={`add-shift-${empId}-${dateStr}`}
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
        <span>{displayEmployees.length} employees</span>
        <span>{shifts.length} shifts</span>
        <span>Total hours: {Object.values(empTotals).reduce((s, t) => s + t.hours, 0).toFixed(2)}</span>
        {showWages && <span>Total wages: ${Object.values(empTotals).reduce((s, t) => s + t.wage, 0).toFixed(2)}</span>}
      </div>
        </>
      )}

      {/* ===== TIME WORKED TAB ===== */}
      {activeTab === "timeworked" && (
        <>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="timeworked-grid">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-600 px-3 py-2.5 w-36 sticky left-0 bg-slate-50 z-10">Employee</th>
                  <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 w-16">Hours</th>
                  {showWages && <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 w-16">Wage</th>}
                  {weekDates.map((d) => (
                    <th key={fmtDate(d)} className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">
                      <div>{fmtDay(d)}</div>
                      <div className="text-[10px] font-normal text-slate-400">{fmtDateShort(d)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {twDisplayEmployees.length === 0 ? (
                  <tr><td colSpan={9 + (showWages ? 1 : 0)} className="text-center py-12 text-slate-400 text-sm">No time clock data for this week</td></tr>
                ) : twDisplayEmployees.map((emp) => {
                  const empId = emp.user_id;
                  const totals = twEmpTotals[empId] || { hours: 0, wage: 0 };
                  return (
                    <tr key={empId} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`tw-row-${empId}`}>
                      <td className="px-3 py-2 sticky left-0 bg-white z-10">
                        <div className="text-sm font-medium text-slate-800 truncate max-w-[130px]">
                          {emp.last_name && emp.first_name ? `${emp.last_name}, ${emp.first_name}` : emp.name}
                        </div>
                      </td>
                      <td className="text-center text-xs font-bold text-slate-700 px-2">{totals.hours > 0 ? totals.hours.toFixed(2) : "-"}</td>
                      {showWages && <td className="text-center text-xs font-bold text-slate-700 px-2">{totals.wage > 0 ? `$${totals.wage.toFixed(2)}` : "-"}</td>}
                      {weekDates.map((d) => {
                        const dateStr = fmtDate(d);
                        const dayShifts = twByEmpDay[empId]?.[dateStr] || [];
                        return (
                          <td key={dateStr} className="px-1 py-1.5 align-top min-w-[110px]">
                            <div className="space-y-1 cursor-pointer" onClick={() => openTWEdit(emp, dateStr)} data-testid={`tw-cell-click-${empId}-${dateStr}`}>
                              {dayShifts.length === 0 && <div className="text-center text-slate-300 text-xs py-1 hover:text-slate-500 hover:bg-slate-50 rounded">-</div>}
                              {dayShifts.map((ws) => {
                                const isOpen = ws.clock_out === null;
                                return (
                                  <div key={ws.shift_id}
                                    className={`rounded px-1.5 py-1 text-[11px] hover:ring-2 hover:ring-blue-300 transition-all ${isOpen ? "bg-emerald-50 border border-emerald-300 text-emerald-800" : "bg-slate-100 border border-slate-200 text-slate-700"}`}
                                    data-testid={`tw-cell-${ws.shift_id}`}
                                  >
                                    <div className="font-medium">
                                      {fmtClockTime(ws.clock_in)} - {isOpen ? <span className="font-bold text-emerald-600">NOW</span> : fmtClockTime(ws.clock_out)}
                                    </div>
                                    {ws.pos_role_name && <div className="text-[9px] text-slate-500">{ws.pos_role_name}</div>}
                                    {!isOpen && ws.hours_worked > 0 && <div className="text-[9px] text-slate-400">{ws.hours_worked.toFixed(2)}h</div>}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Time Worked Summary */}
          <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
            <span>{twDisplayEmployees.length} employees</span>
            <span>{workedShifts.length} clock entries</span>
            <span>Total hours: {Object.values(twEmpTotals).reduce((s, t) => s + t.hours, 0).toFixed(2)}</span>
            {showWages && <span>Total wages: ${Object.values(twEmpTotals).reduce((s, t) => s + t.wage, 0).toFixed(2)}</span>}
          </div>
          {/* Legend */}
          <div className="mt-3 flex items-center gap-4 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-50 border border-emerald-300 inline-block" /> Open Shift (NOW)</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-100 border border-slate-200 inline-block" /> Completed Shift</span>
          </div>
        </>
      )}

      {/* ===== PAYROLL TAB ===== */}
      {activeTab === "payroll" && (() => {
        const maxRegDay = timesheetRules.max_regular_hours_day || 16;
        const maxOTDay = timesheetRules.max_overtime_hours_day || 4;
        const otMult = timesheetRules.overtime_multiplier || 1.5;
        const dtMult = timesheetRules.doubletime_multiplier || 1.5;
        const maxRegWeek = timesheetRules.max_regular_hours_week || 40;
        const showDeclaredTips = timesheetRules.display_declared_tips_payroll !== false;
        const showPaymentTips = timesheetRules.display_payment_tips_payroll !== false;

        // Group worked shifts by employee, then by date
        const empPayroll = {};
        for (const emp of filteredEmployees) {
          empPayroll[emp.user_id] = { employee: emp, dailyHours: {}, totalHours: 0, rate: 0, declaredTips: 0, ccTips: 0 };
          if (emp.roles_wages?.length > 0) {
            empPayroll[emp.user_id].rate = emp.roles_wages[0].hourly_rate || 0;
          }
        }
        for (const ws of workedShifts) {
          if (!empPayroll[ws.employee_id]) continue;
          const ciDate = ws.clock_in ? ws.clock_in.split("T")[0] : null;
          if (!ciDate) continue;
          if (!empPayroll[ws.employee_id].dailyHours[ciDate]) empPayroll[ws.employee_id].dailyHours[ciDate] = 0;
          empPayroll[ws.employee_id].dailyHours[ciDate] += ws.hours_worked || 0;
          if (ws.hourly_rate > 0) empPayroll[ws.employee_id].rate = ws.hourly_rate;
          empPayroll[ws.employee_id].declaredTips += ws.cash_tips_declared || 0;
          empPayroll[ws.employee_id].ccTips += ws.labor_cost > 0 ? 0 : 0; // CC tips come from orders
        }

        // Calculate regular/OT/DT per employee
        const payrollRows = [];
        let grandRegH = 0, grandOTH = 0, grandDTH = 0, grandRegP = 0, grandOTP = 0, grandDTP = 0, grandTotalH = 0, grandTotalP = 0, grandDeclTips = 0, grandCCTips = 0;
        for (const empId of Object.keys(empPayroll)) {
          const ep = empPayroll[empId];
          const emp = ep.employee;
          const rate = ep.rate;
          let weekRegular = 0, weekOT = 0, weekDT = 0;
          // Process each day
          const sortedDates = Object.keys(ep.dailyHours).sort();
          for (const d of sortedDates) {
            const dayTotal = ep.dailyHours[d];
            const dayReg = Math.min(dayTotal, maxRegDay);
            const dayRemaining = dayTotal - dayReg;
            const dayOT = Math.min(dayRemaining, maxOTDay);
            const dayDT = Math.max(0, dayRemaining - maxOTDay);
            weekRegular += dayReg;
            weekOT += dayOT;
            weekDT += dayDT;
          }
          // Cap weekly regular hours
          if (weekRegular > maxRegWeek) {
            const excess = weekRegular - maxRegWeek;
            weekRegular = maxRegWeek;
            weekOT += excess; // Excess regular becomes OT
          }
          weekRegular = Math.round(weekRegular * 100) / 100;
          weekOT = Math.round(weekOT * 100) / 100;
          weekDT = Math.round(weekDT * 100) / 100;
          const totalH = weekRegular + weekOT + weekDT;
          const regPay = Math.round(weekRegular * rate * 100) / 100;
          const otPay = Math.round(weekOT * rate * otMult * 100) / 100;
          const dtPay = Math.round(weekDT * rate * dtMult * 100) / 100;
          const totalPay = regPay + otPay + dtPay;
          if (hideEmpty && totalH === 0) continue;
          const declTips = Math.round((ep.declaredTips || 0) * 100) / 100;
          const ccTips = Math.round((ep.ccTips || 0) * 100) / 100;
          payrollRows.push({ emp, rate, weekRegular, weekOT, weekDT, totalH, regPay, otPay, dtPay, totalPay, declTips, ccTips });
          grandRegH += weekRegular; grandOTH += weekOT; grandDTH += weekDT;
          grandRegP += regPay; grandOTP += otPay; grandDTP += dtPay;
          grandTotalH += totalH; grandTotalP += totalPay;
          grandDeclTips += declTips; grandCCTips += ccTips;
        }

        return (
          <>
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="payroll-grid">
              <table className="w-full min-w-[950px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-600 px-3 py-2.5 sticky left-0 bg-slate-50 z-10">Employee</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">Role</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">Wage</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 bg-blue-50/50">Reg. h.</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 bg-amber-50/50">OT h.</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 bg-red-50/50">DT h.</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 bg-blue-50/50">Reg. Pay</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 bg-amber-50/50">OT Pay</th>
                    <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5 bg-red-50/50">DT Pay</th>
                    <th className="text-center text-xs font-bold text-slate-700 px-2 py-2.5">Total h.</th>
                    <th className="text-center text-xs font-bold text-slate-700 px-2 py-2.5">Total Pay</th>
                    {showDeclaredTips && <th className="text-center text-xs font-semibold text-emerald-700 px-2 py-2.5 bg-emerald-50/50">Declared Tips</th>}
                    {showPaymentTips && <th className="text-center text-xs font-semibold text-purple-700 px-2 py-2.5 bg-purple-50/50">CC Tips</th>}
                  </tr>
                </thead>
                <tbody>
                  {payrollRows.length === 0 ? (
                    <tr><td colSpan={11 + (showDeclaredTips ? 1 : 0) + (showPaymentTips ? 1 : 0)} className="text-center py-12 text-slate-400 text-sm">No payroll data for this period</td></tr>
                  ) : (
                    <>
                      {payrollRows.map((r) => {
                        const empName = r.emp.last_name && r.emp.first_name ? `${r.emp.last_name}, ${r.emp.first_name}` : r.emp.name;
                        const roleName = r.emp.roles_wages?.[0]?.pos_role_id ? posRoles.find((pr) => pr.pos_role_id === r.emp.roles_wages[0].pos_role_id)?.name || "" : "";
                        return (
                          <tr key={r.emp.user_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`payroll-row-${r.emp.user_id}`}>
                            <td className="px-3 py-2.5 sticky left-0 bg-white z-10 text-sm font-medium text-slate-800">{empName}</td>
                            <td className="text-center text-xs text-slate-500 px-2">{roleName}</td>
                            <td className="text-center text-xs font-mono text-slate-600 px-2">{r.rate > 0 ? `$${r.rate.toFixed(2)}` : "-"}</td>
                            <td className="text-center text-xs font-mono px-2">{r.weekRegular > 0 ? r.weekRegular.toFixed(2) : "0.00"}</td>
                            <td className="text-center text-xs font-mono px-2 text-amber-700">{r.weekOT > 0 ? r.weekOT.toFixed(2) : "0.00"}</td>
                            <td className="text-center text-xs font-mono px-2 text-red-600">{r.weekDT > 0 ? r.weekDT.toFixed(2) : "0.00"}</td>
                            <td className="text-center text-xs font-mono px-2">{r.regPay > 0 ? `$${r.regPay.toFixed(2)}` : "$0.00"}</td>
                            <td className="text-center text-xs font-mono px-2 text-amber-700">{r.otPay > 0 ? `$${r.otPay.toFixed(2)}` : "$0.00"}</td>
                            <td className="text-center text-xs font-mono px-2 text-red-600">{r.dtPay > 0 ? `$${r.dtPay.toFixed(2)}` : "$0.00"}</td>
                            <td className="text-center text-sm font-bold text-slate-800 px-2">{r.totalH.toFixed(2)}</td>
                            <td className="text-center text-sm font-bold text-slate-900 px-2">${r.totalPay.toFixed(2)}</td>
                            {showDeclaredTips && <td className="text-center text-xs font-mono text-emerald-700 px-2">{r.declTips > 0 ? `$${r.declTips.toFixed(2)}` : "$0.00"}</td>}
                            {showPaymentTips && <td className="text-center text-xs font-mono text-purple-700 px-2">{r.ccTips > 0 ? `$${r.ccTips.toFixed(2)}` : "$0.00"}</td>}
                          </tr>
                        );
                      })}
                      {/* Totals Row */}
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                        <td className="px-3 py-2.5 sticky left-0 bg-slate-50 z-10 text-sm text-slate-700" colSpan={3}>Totals:</td>
                        <td className="text-center text-xs font-mono px-2">{grandRegH.toFixed(2)}</td>
                        <td className="text-center text-xs font-mono px-2 text-amber-700">{grandOTH.toFixed(2)}</td>
                        <td className="text-center text-xs font-mono px-2 text-red-600">{grandDTH.toFixed(2)}</td>
                        <td className="text-center text-xs font-mono px-2">${grandRegP.toFixed(2)}</td>
                        <td className="text-center text-xs font-mono px-2 text-amber-700">${grandOTP.toFixed(2)}</td>
                        <td className="text-center text-xs font-mono px-2 text-red-600">${grandDTP.toFixed(2)}</td>
                        <td className="text-center text-sm font-bold text-slate-800 px-2">{grandTotalH.toFixed(2)}</td>
                        <td className="text-center text-sm font-bold text-slate-900 px-2">${grandTotalP.toFixed(2)}</td>
                        {showDeclaredTips && <td className="text-center text-xs font-mono text-emerald-700 px-2">${grandDeclTips.toFixed(2)}</td>}
                        {showPaymentTips && <td className="text-center text-xs font-mono text-purple-700 px-2">${grandCCTips.toFixed(2)}</td>}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
            {/* Payroll Notes */}
            <div className="mt-3 text-[10px] text-slate-400 space-y-0.5">
              <p>* Regular hours capped at {maxRegWeek}h/week, {maxRegDay}h/day. Overtime at {otMult}x rate (max {maxOTDay}h/day). Doubletime at {dtMult}x rate.</p>
              <p>* Report includes open shifts. See Time Worked for details.</p>
            </div>
          </>
        );
      })()}

      {/* ===== TIP POOLING TAB ===== */}
      {activeTab === "tippool" && (
        <>
          {tipPoolLoading ? (
            <div className="text-center py-12 text-slate-400">Calculating tip pool...</div>
          ) : tipPoolData ? (
            <>
              <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="tippool-grid">
                <table className="w-full min-w-[1000px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left text-xs font-semibold text-slate-600 px-3 py-2.5 sticky left-0 bg-slate-50 z-10">Name</th>
                      <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">Role</th>
                      <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">Hours</th>
                      <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">Sales</th>
                      <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">Declared Tips</th>
                      <th className="text-center text-xs font-semibold text-slate-600 px-2 py-2.5">Payment Tips</th>
                      <th className="text-center text-xs font-semibold text-emerald-700 px-2 py-2.5 bg-emerald-50/50">Tip In Amt.</th>
                      <th className="text-center text-xs font-semibold text-blue-700 px-2 py-2.5 bg-blue-50/50">Tip Share Amt.</th>
                      <th className="text-center text-xs font-semibold text-blue-700 px-2 py-2.5 bg-blue-50/50">Adj. Tip Share</th>
                      <th className="text-center text-xs font-bold text-slate-700 px-2 py-2.5">Final Tips</th>
                      <th className="text-center text-xs font-bold text-slate-700 px-2 py-2.5">Tips Due</th>
                      <th className="text-center text-xs font-bold text-slate-700 px-2 py-2.5">Tip Pool Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tipPoolData.rows.length === 0 ? (
                      <tr><td colSpan={12} className="text-center py-12 text-slate-400 text-sm">No tip pool data for this period</td></tr>
                    ) : tipPoolData.rows.map((r) => (
                      <tr key={r.employee_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`tippool-row-${r.employee_id}`}>
                        <td className="px-3 py-2.5 sticky left-0 bg-white z-10 text-sm font-medium text-slate-800">{r.employee_name}</td>
                        <td className="text-center text-xs text-slate-500 px-2">{r.pos_role_name || "-"}</td>
                        <td className="text-center text-xs font-mono text-slate-600 px-2">{r.hours > 0 ? r.hours.toFixed(2) : "-"}</td>
                        <td className="text-center text-xs font-mono text-slate-600 px-2">{r.sales > 0 ? `$${r.sales.toFixed(2)}` : "-"}</td>
                        <td className="text-center text-xs font-mono text-slate-600 px-2">{r.declared_tips > 0 ? `$${r.declared_tips.toFixed(2)}` : "0"}</td>
                        <td className="text-center text-xs font-mono text-emerald-600 px-2">{r.payment_tips > 0 ? `$${r.payment_tips.toFixed(2)}` : "0"}</td>
                        <td className="text-center text-xs font-mono text-emerald-700 px-2">{r.tip_in_amt > 0 ? `$${r.tip_in_amt.toFixed(2)}` : "0"}</td>
                        <td className="text-center text-xs font-mono text-blue-700 px-2">{r.tip_share_amt > 0 ? `$${r.tip_share_amt.toFixed(2)}` : "0"}</td>
                        <td className="text-center text-xs font-mono text-blue-700 px-2">{r.adj_tip_share > 0 ? `$${r.adj_tip_share.toFixed(2)}` : "0"}</td>
                        <td className="text-center text-sm font-bold text-slate-800 px-2">{r.final_tips > 0 ? `$${r.final_tips.toFixed(2)}` : "0"}</td>
                        <td className="text-center text-sm font-bold text-slate-800 px-2">{r.tips_due_employee > 0 ? `$${r.tips_due_employee.toFixed(2)}` : "0"}</td>
                        <td className={`text-center text-sm font-bold px-2 ${r.tip_pool_result > 0 ? "text-emerald-600" : r.tip_pool_result < 0 ? "text-red-600" : "text-slate-500"}`}>
                          {r.tip_pool_result > 0 ? `$${r.tip_pool_result.toFixed(2)}` : r.tip_pool_result < 0 ? `-$${Math.abs(r.tip_pool_result).toFixed(2)}` : "0"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Tip Pool Summary */}
              <div className="mt-4 flex items-center gap-6 p-4 bg-white border border-slate-200 rounded-lg" data-testid="tippool-summary">
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase">Calculated Tip Pool</p>
                  <p className="text-lg font-bold text-slate-900">${tipPoolData.calculated_tip_pool.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase">Tip Pool Adj.</p>
                  <Input type="number" step="0.01" value={tipPoolAdj}
                    onChange={(e) => setTipPoolAdj(parseFloat(e.target.value) || 0)}
                    className="h-8 w-24 text-xs font-mono" data-testid="tippool-adj-input" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase">Final Tip Pool</p>
                  <p className="text-lg font-bold text-slate-900">${(tipPoolData.calculated_tip_pool + tipPoolAdj).toFixed(2)}</p>
                </div>
                <div className="ml-auto">
                  <Button variant="outline" size="sm" onClick={fetchTipPool} className="text-xs" data-testid="tippool-recalculate">
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Recalculate
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">Select a period and click Recalculate</div>
          )}
        </>
      )}

      {/* Shift Create/Edit Dialog */}
      <Dialog open={showShiftDialog} onOpenChange={setShowShiftDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle>{editingShift ? "Edit Shift" : "New Shift"}</DialogTitle>
            <DialogDescription>{editingShift ? "Modify shift details" : "Schedule a new shift"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Employee</Label>
              <Select value={shiftForm.employee_id} onValueChange={(v) => setShiftForm({ ...shiftForm, employee_id: v })}>
                <SelectTrigger className="mt-1" data-testid="shift-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.user_id} value={e.user_id}>{e.last_name && e.first_name ? `${e.last_name}, ${e.first_name}` : e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={shiftForm.date} onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })} className="mt-1" data-testid="shift-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} className="mt-1" data-testid="shift-start-input" />
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} className="mt-1" data-testid="shift-end-input" />
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={shiftForm.pos_role_id || "_none"} onValueChange={(v) => setShiftForm({ ...shiftForm, pos_role_id: v === "_none" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="shift-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No specific role</SelectItem>
                  {posRoles.map((r) => <SelectItem key={r.pos_role_id} value={r.pos_role_id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={shiftForm.notes} onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })} placeholder="Optional notes" className="mt-1" data-testid="shift-notes-input" />
            </div>
            {shiftForm.start_time && shiftForm.end_time && (
              <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs text-slate-600">
                <Clock className="h-3 w-3 inline mr-1" />
                {calcHours(shiftForm.start_time, shiftForm.end_time).toFixed(2)} hours ({to12h(shiftForm.start_time)} - {to12h(shiftForm.end_time)})
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShiftDialog(false)}>Cancel</Button>
            <Button onClick={saveShift} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="shift-save-btn">
              {editingShift ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Shifts Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="copy-shifts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5" /> Copy Shifts</DialogTitle>
            <DialogDescription>Copy all shifts from this week ({fmtDateShort(weekDates[0])} - {fmtDateShort(weekDates[6])}) to another week.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Target Week Start (Sunday)</Label>
              <Input type="date" value={copyTarget} onChange={(e) => setCopyTarget(e.target.value)} className="mt-1" data-testid="copy-target-input" />
            </div>
            <p className="text-xs text-slate-500">{shifts.length} shifts will be copied to the target week.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyDialog(false)}>Cancel</Button>
            <Button onClick={handleCopy} disabled={copying} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="copy-confirm-btn">
              {copying ? "Copying..." : "Copy Shifts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> Import Shifts</DialogTitle>
            <DialogDescription>Upload a CSV file with shift data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
              <p className="font-semibold text-slate-700">CSV Format:</p>
              <code className="block bg-white p-2 rounded border border-slate-200 text-[10px] font-mono">
                employee_name,date,start_time,end_time,role_name<br />
                John Smith,2026-04-20,09:00,17:00,Front End<br />
                Jane Doe,2026-04-20,16:00,22:00,Chef
              </code>
              <p className="text-slate-500 mt-1">Employee names must match existing employees. Role name is optional.</p>
            </div>
            <div>
              <input type="file" accept=".csv" ref={fileInputRef} onChange={handleImport} className="hidden" />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} className="w-full" data-testid="import-file-btn">
                <Upload className="h-4 w-4 mr-2" /> {importing ? "Importing..." : "Choose CSV File"}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Worked Edit Dialog */}
      <Dialog open={showTWEdit} onOpenChange={setShowTWEdit}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tw-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {twEditEmp?.employee?.last_name && twEditEmp?.employee?.first_name
                ? `${twEditEmp.employee.last_name}, ${twEditEmp.employee.first_name}`
                : twEditEmp?.employee?.name} | {twEditEmp?.date ? new Date(twEditEmp.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "2-digit", day: "2-digit", year: "numeric" }) : ""}
            </DialogTitle>
            <DialogDescription>Adjust clock-in/out times, roles, and wages for this day.</DialogDescription>
          </DialogHeader>

          {twEditShifts.filter((s) => !s._isNew).length === 0 && twEditShifts.filter((s) => s._isNew).length === 0 && (
            <p className="text-sm text-slate-400 py-4 text-center">No clock entries for this day.</p>
          )}
          {twEditShifts.length > 0 && (
            <div className="space-y-2 py-2">
              {/* Column Headers */}
              <div className="grid grid-cols-[80px_80px_90px_100px_70px_1fr_40px] gap-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-1">
                <span>Start Time</span><span>End Time</span><span>Status</span><span>Role</span><span>Wage</span><span>Remarks</span><span></span>
              </div>
              {twEditShifts.map((s, idx) => (
                <div key={s.shift_id}
                  className={`grid grid-cols-[80px_80px_90px_100px_70px_1fr_40px] gap-2 items-center p-2 rounded-lg border transition-colors ${s._deleted ? "bg-red-50 border-red-200 opacity-50" : s._isNew ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}
                  data-testid={`tw-edit-row-${s.shift_id}`}
                >
                  <Input type="time" value={isoToTimeInput(s.clock_in)} disabled={s._deleted}
                    onChange={(e) => twUpdateField(idx, "clock_in", timeInputToIso(e.target.value, s.clock_in))}
                    className="h-8 text-xs" data-testid={`tw-edit-start-${idx}`} />
                  <Input type="time" value={s._isOpen ? "" : isoToTimeInput(s.clock_out)} disabled={s._deleted || s._isOpen}
                    placeholder={s._isOpen ? "NOW" : ""}
                    onChange={(e) => twUpdateField(idx, "clock_out", timeInputToIso(e.target.value, s.clock_out || s.clock_in))}
                    className="h-8 text-xs" data-testid={`tw-edit-end-${idx}`} />
                  <Badge variant="outline" className={`text-[10px] h-6 justify-center ${s._isNew ? "border-blue-300 text-blue-700 bg-blue-50" : s._isOpen ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-600"}`}>
                    {s._isNew ? "New" : s._isOpen ? "Clocked In" : "Clock Out"}
                  </Badge>
                  <Select value={s.pos_role_id || "_none"} disabled={s._deleted}
                    onValueChange={(v) => twUpdateField(idx, "pos_role_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className="h-8 text-[11px]" data-testid={`tw-edit-role-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">—</SelectItem>
                      {posRoles.map((r) => <SelectItem key={r.pos_role_id} value={r.pos_role_id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="0.01" min="0" value={s.hourly_rate} disabled={s._deleted}
                    onChange={(e) => twUpdateField(idx, "hourly_rate", e.target.value)}
                    className="h-8 text-xs" data-testid={`tw-edit-wage-${idx}`} />
                  <Input value={s.remarks || ""} placeholder="Remarks" disabled={s._deleted}
                    onChange={(e) => twUpdateField(idx, "remarks", e.target.value)}
                    className="h-8 text-xs" data-testid={`tw-edit-remarks-${idx}`} />
                  <button onClick={() => s._isNew ? setTwEditShifts((prev) => prev.filter((_, i) => i !== idx)) : twMarkDelete(idx)}
                    className={`text-xs font-medium ${s._deleted ? "text-emerald-600 hover:text-emerald-700" : "text-red-500 hover:text-red-700"}`}
                    data-testid={`tw-edit-delete-${idx}`}>
                    {s._isNew ? "remove" : s._deleted ? "undo" : "delete"}
                  </button>
                </div>
              ))}
            </div>
          )}

          <Button variant="outline" size="sm" onClick={twAddNew} className="mt-1" data-testid="tw-edit-add-new">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add New
          </Button>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowTWEdit(false)} data-testid="tw-edit-cancel">Cancel</Button>
            <Button onClick={twSaveAll} disabled={twSaving || twEditShifts.length === 0}
              className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="tw-edit-save">
              {twSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
